import {
  Button,
  Link,
  Typography /** Media query result is used within a client-only modal; this is safe to use. */,
  // eslint-disable-next-line no-restricted-imports
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { useMatches } from 'react-router';

import { FeelingIconMap } from '@jane/bloom/components/iconMaps/feelingIconMap';
import {
  useProductSearchFilters,
  useSearchParamFilter,
} from '@jane/bloom/hooks/filters';
import { FilterCategoryLabel } from '@jane/bloom/hooks/filters/useProductSearchFilters';
import {
  customRowPages,
  isRouteInGroup,
} from '@jane/bloom/utils/routes/routeIds';
import type {
  FilterTypeKeys,
  ProductSearchFiltersViewModel,
} from '@jane/bloom/viewModels/productSearchFilters';

import { ModalSection } from './modalSection';

import styles from './buttonFilterSection.module.css';

const MOBILE_LABEL_LENGTH = 40;

const DESKTOP_LABEL_LENGTH = 20;

const THREE_ITEMS_CATEGORIES = ['categories', 'terpenes'];

const THREE_ITEMS_FULL_CATEGORIES = ['specials'];

const FOUR_ITEMS_CATEGORIES = ['lineages', 'feelings', 'activities'];

const FIVE_ITEMS_CATEGORIES = ['prices', 'weights'];

type ModalButtonFilterSectionProps = {
  filterKey: FilterTypeKeys;
  icon?: boolean;
  shouldShowDividerBelow?: boolean;
  shouldShowGold?: boolean;
};

export function ModalButtonFilterSection({
  filterKey,
  icon,
  shouldShowDividerBelow,
  shouldShowGold,
}: ModalButtonFilterSectionProps) {
  const isCustomRow = Boolean(
    useMatches().find(({ id }) => isRouteInGroup(customRowPages, id))
  );
  const { clearFilter, getFilterStatus, setFilter } =
    useSearchParamFilter(filterKey);

  const isMobile = useMobileMediaQuery({});

  const {
    getProductSearchFilterOptions,
    getProductSubcategories,
    selectedCategory,
  } = useProductSearchFilters();

  const productSearchFilterOptions = getProductSearchFilterOptions(
    filterKey as keyof ProductSearchFiltersViewModel
  );

  const [collapsed, setCollapsed] = useState(true);

  const filterOptions = useMemo(() => {
    let options =
      selectedCategory && filterKey === 'categories'
        ? getProductSubcategories(selectedCategory)
        : productSearchFilterOptions;

    if (filterKey === 'categories' && shouldShowGold) {
      options = options.filter((subcategory) => subcategory.value !== 'sale');
    }

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, filterKey, shouldShowGold, productSearchFilterOptions]);

  const dynamicFilterOptions = useMemo(() => {
    if (!filterOptions) {
      return [];
    }

    return collapsed && isMobile ? filterOptions.slice(0, 4) : filterOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, filterOptions]);

  const isCollapsible = isMobile && filterOptions && filterOptions.length > 4;

  const sectionLabel =
    selectedCategory && filterKey === 'categories'
      ? 'Subcategory'
      : FilterCategoryLabel[filterKey];

  // Custom rows overlap with how other categories are modeled.
  if (isCustomRow && filterKey === 'categories') {
    return null;
  }

  return dynamicFilterOptions.length > 0 ? (
    <ModalSection
      shouldShowDividerBelow={shouldShowDividerBelow}
      filterLabel={sectionLabel}
      deselectFilters={clearFilter}
    >
      <div
        className={clsx(styles['buttons'], {
          [styles['buttonGrid--3']]: THREE_ITEMS_CATEGORIES.includes(filterKey),
          [styles['buttonGrid--3-full']]:
            THREE_ITEMS_FULL_CATEGORIES.includes(filterKey),
          [styles['buttonGrid--4']]: FOUR_ITEMS_CATEGORIES.includes(filterKey),
          [styles['buttonGrid--5']]: FIVE_ITEMS_CATEGORIES.includes(filterKey),
        })}
      >
        {dynamicFilterOptions.map((item) => {
          const currentValue = getFilterStatus(item.value) === 'selected';
          const labelWithCount = `${item.label} (${item.count})`;
          const mobileLabel =
            labelWithCount.length > MOBILE_LABEL_LENGTH
              ? labelWithCount.substring(0, MOBILE_LABEL_LENGTH) + '...'
              : labelWithCount;
          const desktopLabel =
            labelWithCount.length > DESKTOP_LABEL_LENGTH
              ? labelWithCount.substring(0, DESKTOP_LABEL_LENGTH) + '...'
              : labelWithCount;

          return (
            <Button
              key={`${filterKey}${item.value}`}
              className={styles['button']}
              full
              size="large"
              startIcon={icon && FeelingIconMap[item.value]}
              alignment="left"
              label={
                <>
                  <Typography
                    className={styles['buttonLabel--desktop']}
                    color="inherit"
                    variant="body-semibold"
                  >
                    {desktopLabel}
                  </Typography>

                  <Typography
                    className={styles['buttonLabel--mobile']}
                    color="inherit"
                    variant="body-semibold"
                  >
                    {mobileLabel}
                  </Typography>
                </>
              }
              variant={
                currentValue === !!item.value ? 'tertiary-selected' : 'tertiary'
              }
              disabled={item.count === 0}
              onClick={() => {
                setFilter(
                  item.value,
                  currentValue === undefined ? true : !currentValue
                );
              }}
            />
          );
        })}
      </div>
      {isCollapsible && (
        <Link
          mt={16}
          color="text-main"
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          {collapsed ? 'View more' : 'View less'}
        </Link>
      )}
    </ModalSection>
  ) : null;
}
